import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Cross-cutting UI-session flags read across every layer (map status bar,
 * wind-map overlay, idle/rotation stack) — not widget/layout management. Live in
 * `shared` so selectors/actions import downward. Persisted via
 * `uiSessionPersistConfig`, which blacklists the transient highlight selection.
 */
export interface UiSessionState {
  highlightOn: boolean;
  rotationOn: boolean;
  windRotationOn: boolean;
  weatherAnimationOn: boolean;
  carouselMs: number;
  globalSearch: string;
  userIdleMs: number;
  preferredSorting: 'asc' | 'desc';
  highlightedAircraftRegistration: string | null;
  highlightedAirports: string[];
  /**
   * Cross-widget search string, read by the map Searchbar's case-filter hooks.
   * Blacklisted from persistence, so it resets on reload. Distinct from
   * `globalSearch` (a persisted setting with no current readers).
   */
  searchText: string;
  /**
   * The served region the map opens in, chosen by the operator and persisted.
   * `null` until they pick one, which is ABSENT rather than "no region" — but
   * the map does not keep its own opening view either way: `MapCameraContext`
   * resolves `chosenRegionId ?? defaultRegionId` and falls back to the first
   * served region when that resolves to nothing, so with no preference set the
   * map still opens framed on whichever region the geo library returns first.
   *
   * Holds a geo-library service region id, which is environment-specific — a
   * preference saved against dev will not resolve on stage, and an id this
   * environment does not serve hits the same first-region fallback as having
   * no preference at all, rather than reading as an error.
   */
  defaultRegionId: string | null;
}

const initialState: UiSessionState = {
  highlightOn: true,
  rotationOn: false,
  windRotationOn: false,
  // Default ON: the wind map's forecast time-lapse auto-starts on first load
  // (TSMED-949). No settings persist, so initialState IS the first-load state.
  weatherAnimationOn: true,
  carouselMs: 5000,
  globalSearch: '',
  userIdleMs: 30000,
  preferredSorting: 'asc',
  highlightedAircraftRegistration: null,
  highlightedAirports: [],
  searchText: '',
  defaultRegionId: null,
};

export const uiSessionSlice = createSlice({
  name: 'ui/session',
  initialState,
  reducers: {
    setHighlightOn: (s, a: PayloadAction<boolean>) => {
      s.highlightOn = a.payload;
    },
    setRotationOn: (s, a: PayloadAction<boolean>) => {
      s.rotationOn = a.payload;
    },
    setWindRotationOn: (s, a: PayloadAction<boolean>) => {
      s.windRotationOn = a.payload;
    },
    setWeatherAnimationOn: (s, a: PayloadAction<boolean>) => {
      s.weatherAnimationOn = a.payload;
    },
    setCarouselMs: (s, a: PayloadAction<number>) => {
      s.carouselMs = a.payload;
    },
    setGlobalSearch: (s, a: PayloadAction<string>) => {
      s.globalSearch = a.payload;
    },
    setUserIdleMs: (s, a: PayloadAction<number>) => {
      s.userIdleMs = a.payload;
    },
    setPreferredSorting: (s, a: PayloadAction<'asc' | 'desc'>) => {
      s.preferredSorting = a.payload;
    },
    setSearchText: (s, a: PayloadAction<string>) => {
      s.searchText = a.payload;
    },
    // Equality guards below prevent no-op dispatches from producing new state
    // references. React 19's useSyncExternalStore triggers forceStoreRerender on
    // every snapshot change, so the wind-map overlay effects that dispatch these
    // on mount/interval would cascade into "Maximum update depth exceeded"
    // without them.
    setDefaultRegionId: (s, a: PayloadAction<string | null>) => {
      // Immer's set trap already short-circuits an identical primitive, so this guard
      // is for consistency with neighbouring reducers rather than the mechanism that
      // prevents a new state reference.
      if (s.defaultRegionId === a.payload) {
        return;
      }
      s.defaultRegionId = a.payload;
    },
    setHighlightedAircraftRegistration: (
      s,
      a: PayloadAction<string | null>,
    ) => {
      if (s.highlightedAircraftRegistration === a.payload) {
        return;
      }
      s.highlightedAircraftRegistration = a.payload;
    },
    setHighlightedAirports: (s, a: PayloadAction<string[]>) => {
      if (
        s.highlightedAirports.length === a.payload.length &&
        s.highlightedAirports.every((v, i) => v === a.payload[i])
      ) {
        return;
      }
      s.highlightedAirports = a.payload;
    },
  },
});

export const uiSessionActions = uiSessionSlice.actions;
